import type { Transaction, Table } from 'dexie'
import type { PosDatabase } from '@core/db/db'
import type {
  Product,
  StockMovement,
  StockMovementType,
  Writeoff,
  WriteoffItem,
  WriteoffReasonCode
} from '@core/db/models'
import { generateUUID } from '@core/utils/crypto'

/** Входное складское движение. */
export interface StockMovementInput {
  type: StockMovementType
  barcode: string
  /** Знаковое количество: продажа/списание/отмена — минус, возврат — плюс. */
  qty: number
  lineId?: string
  reasonCode?: WriteoffReasonCode | string
  comment?: string
  receiptId?: string
  writeoffId?: string
  zoneId?: string
  userId?: string
}

/** Позиция для складского движения (совместима с SaleReceiptItem). */
export interface StockItem {
  barcode: string
  qty: number
  lineId?: string
}

/**
 * Складской ledger (ARCH-TASK-65.10).
 *
 * Источник истины — `stock_movements`; `Product.stock` обновляется как кэш
 * в той же транзакции. Это позволяет отменять/возвращать/списывать без
 * расхождений и строить отчёты по движениям.
 */
export class StockService {
  constructor(private readonly db: PosDatabase) {}

  /** Записать одно движение и обновить кэш остатка. */
  async applyMovement(input: StockMovementInput, tx?: Transaction): Promise<void> {
    if (!input.barcode || !Number.isFinite(input.qty) || input.qty === 0) return
    const run = async (t: Transaction): Promise<void> => {
      const movements = t.table('stock_movements') as Table<StockMovement, number>
      const products = t.table('products') as Table<Product, number>

      // 65.10: детерминированный id — повторное применение того же движения
      // (retry после сбоя) не создаёт дубль. Для writeoff — по writeoffId.
      const movementId =
        input.writeoffId && input.lineId
          ? `wf:${input.writeoffId}:${input.lineId}`
          : `${input.type}:${input.receiptId ?? 'manual'}:${input.lineId ?? input.barcode}`
      const existing = await movements.where('movementId').equals(movementId).first()
      if (existing) return

      await movements.add({
        movementId,
        type: input.type,
        barcode: input.barcode,
        lineId: input.lineId,
        qty: input.qty,
        reasonCode: input.reasonCode,
        comment: input.comment,
        receiptId: input.receiptId,
        writeoffId: input.writeoffId,
        zoneId: input.zoneId,
        userId: input.userId,
        createdAt: Date.now(),
        synced: false,
        syncState: 0
      } as StockMovement)

      const product = await products.where('barcode').equals(input.barcode).first()
      if (product?.id != null) {
        const current = typeof product.stock === 'number' ? product.stock : 0
        // Продажа/списание не уходят в минус; возврат/отмена увеличивают остаток.
        const next = input.qty < 0 ? Math.max(0, current + input.qty) : current + input.qty
        await products.update(product.id, {
          stock: next,
          updatedAt: Date.now()
        })
      }
    }

    if (tx) {
      await run(tx)
      return
    }
    await this.db.transaction('rw', this.db.stock_movements, this.db.products, run)
  }

  /** Движения по чеку: продажа (−) или возврат (+). */
  async applyReceiptItems(
    items: StockItem[],
    opts: {
      type: 'sale' | 'return' | 'cancel'
      receiptId: string
      zoneId?: string
      userId?: string
    },
    tx?: Transaction
  ): Promise<void> {
    const sign = opts.type === 'sale' ? -1 : 1
    for (const [idx, item] of items.entries()) {
      if (!item.barcode) continue
      await this.applyMovement(
        {
          type: opts.type,
          barcode: item.barcode,
          qty: sign * item.qty,
          // Для ручных операций без lineId добавляем короткий индекс — иначе
          // две строки одного ШК дали бы одинаковый movementId (и не влезли бы
          // в VARCHAR(64) облака).
          lineId: item.lineId ?? `idx${idx}`,
          receiptId: opts.receiptId,
          zoneId: opts.zoneId,
          userId: opts.userId
        },
        tx
      )
    }
  }

  /**
   * Документ списания + движения (−). Для «возврат + списание» вызывается
   * после возврата: +qty и −qty в одной бизнес-операции, итог 0.
   */
  async createWriteoff(
    input: {
      receiptId?: string
      items: WriteoffItem[]
      reasonCode: WriteoffReasonCode
      /** Источник документа: manual | return_writeoff | marking_hold. */
      origin?: 'manual' | 'return_writeoff' | 'marking_hold'
      comment?: string
      zoneId?: string
      userId?: string
      userName?: string
    },
    tx?: Transaction
  ): Promise<Writeoff> {
    const writeoffId = generateUUID()
    const total = input.items.reduce((sum, i) => sum + i.price * i.qty, 0)
    const doc: Writeoff = {
      writeoffId,
      receiptId: input.receiptId,
      origin: input.origin ?? 'manual',
      reasonCode: input.reasonCode,
      comment: input.comment,
      items: input.items,
      total: Number(total.toFixed(2)),
      userId: input.userId,
      userName: input.userName,
      zoneId: input.zoneId,
      createdAt: Date.now(),
      synced: false,
      syncState: 0
    }

    const run = async (t: Transaction): Promise<Writeoff> => {
      const writeoffs = t.table('writeoffs') as Table<Writeoff, number>
      const id = (await writeoffs.add(doc)) as number
      const saved: Writeoff = { ...doc, id }
      for (const [idx, item] of input.items.entries()) {
        if (!item.barcode) continue
        await this.applyMovement(
          {
            type: 'writeoff',
            barcode: item.barcode,
            qty: -Math.abs(item.qty),
            lineId: item.lineId ?? `idx${idx}`,
            reasonCode: input.reasonCode,
            comment: input.comment,
            receiptId: input.receiptId,
            writeoffId,
            zoneId: input.zoneId,
            userId: input.userId
          },
          t
        )
      }
      return saved
    }

    if (tx) return run(tx)
    return this.db.transaction('rw', this.db.writeoffs, this.db.stock_movements, this.db.products, run)
  }
}
